import L from 'leaflet';
import React from 'react';
import { MapContainer, ImageOverlay, useMap } from 'react-leaflet';
import * as Inovx from '@shared';
import { isScanPositionView } from '../helpers/viewMedia';
import View360Marker from './View360Marker';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

interface FacilityMapProps {
    siteMap: ISiteMap;
    views?: IView360Detail[];
    onViewMarkerClicked?: (view: IView360Detail, index: number) => void;
    onViewMarkerHighlighted?: (view: IView360Detail, index: number, highlighted: boolean) => void,
    showElements: boolean;
    activeView?: IView360Detail;
    activeMarkerAsCircle?: boolean;
    highlightedView?: IView360Detail;
    isMiniMap?: boolean;
    resizeCount?: number;
    viewerDirectionDegrees?: number;
    overridePosition?: IVector3D;
}

type ImageData = {
    src: string;
    width: number;
    height: number;
    imageBounds: L.LatLngBoundsLiteral;
};

type IViewMarkerData = {
    view: IView360Detail;
    imagePosition: IVector2D;
    mapPosition: L.LatLng;
}

// Keep Leaflet's classic animated marker behavior for small maps. At this count
// and above, hiding the complete marker pane during movement is substantially
// smoother than animating every Scan Position and Image 360 marker.
const MARKER_INTERACTION_OPTIMIZATION_THRESHOLD = 100;
// Leaflet uses a 250ms zoom transition after its wheel debounce. Keep the pane
// hidden across the complete input burst instead of revealing it between steps.
const WHEEL_MARKER_REVEAL_DELAY_MS = 350;

const MapMarkerInteractionController = ({ hideMarkersDuringInteraction }: { hideMarkersDuringInteraction: boolean }) => {
    const map = useMap();

    React.useEffect(() => {
        const markerPane = map.getPane('markerPane');
        const mapContainer = map.getContainer();
        if (!markerPane || !mapContainer) return;

        const previousVisibility = markerPane.style.visibility;
        let isMoving = false;
        let isWheelActive = false;
        let isNormalizingPane = false;
        let wheelRevealTimer: number = null;

        const updateMarkerVisibility = () => {
            markerPane.style.visibility = isMoving || isWheelActive
                ? 'hidden'
                : previousVisibility;
        };

        const handleWheel = () => {
            if (!hideMarkersDuringInteraction) return;

            isWheelActive = true;
            updateMarkerVisibility();

            if (wheelRevealTimer != null) {
                window.clearTimeout(wheelRevealTimer);
            }
            wheelRevealTimer = window.setTimeout(() => {
                isWheelActive = false;
                wheelRevealTimer = null;
                updateMarkerVisibility();
            }, WHEEL_MARKER_REVEAL_DELAY_MS);
        };

        const handleMoveStart = () => {
            if (hideMarkersDuringInteraction) {
                isMoving = true;
                updateMarkerVisibility();
            }
        };

        const handleMoveEnd = () => {
            if (isNormalizingPane) return;

            const mapPane = map.getPane('mapPane');
            const mapPanePosition = mapPane ? L.DomUtil.getPosition(mapPane) : null;
            if (mapPanePosition && (mapPanePosition.x !== 0 || mapPanePosition.y !== 0)) {
                // Leaflet normally retains this translation after panning. Rebase the
                // layers while hidden so browser hit-testing is not left under a large
                // transformed subtree containing thousands of interactive markers.
                isNormalizingPane = true;
                const resetOptions: L.ZoomPanOptions & { reset: boolean } = { reset: true };
                map.setView(map.getCenter(), map.getZoom(), resetOptions);
                isNormalizingPane = false;
            }

            isMoving = false;
            updateMarkerVisibility();
        };

        if (hideMarkersDuringInteraction) {
            mapContainer.addEventListener('wheel', handleWheel, { passive: true });
        }
        map.on('movestart', handleMoveStart);
        map.on('moveend', handleMoveEnd);

        return () => {
            if (wheelRevealTimer != null) {
                window.clearTimeout(wheelRevealTimer);
            }
            if (hideMarkersDuringInteraction) {
                mapContainer.removeEventListener('wheel', handleWheel);
            }
            map.off('movestart', handleMoveStart);
            map.off('moveend', handleMoveEnd);
            markerPane.style.visibility = previousVisibility;
        };
    }, [hideMarkersDuringInteraction, map]);

    return null;
};

const FacilityMap: React.FC<FacilityMapProps> = (props) => {
    const defaultZoom = 3;
    const defaultZoomRatio = 2 ** defaultZoom;
    const maxZoom = defaultZoom + 3; // by default 3 zoom-in levels are allowed

    const mapRef = React.useRef<L.Map>(null);
    const [imageData, setImageData] = React.useState<ImageData | null>(null);
    const [imageCenter, setImageCenter] = React.useState<L.LatLng | null>(null);
    const [mapBounds, setMapBounds] = React.useState<L.LatLngBoundsLiteral>(null);
    const [mapViews, setMapViews] = React.useState<IViewMarkerData[]>([]);

    const markerClickedRef = React.useRef(props.onViewMarkerClicked);
    const markerHighlightedRef = React.useRef(props.onViewMarkerHighlighted);

    markerClickedRef.current = props.onViewMarkerClicked;
    markerHighlightedRef.current = props.onViewMarkerHighlighted;

    const handleMarkerClicked = React.useCallback((view: IView360Detail, index: number) => {
        markerClickedRef.current?.(view, index);
    }, []);

    const handleMarkerHighlighted = React.useCallback((view: IView360Detail, index: number, highlighted: boolean) => {
        markerHighlightedRef.current?.(view, index, highlighted);
    }, []);
   
    React.useEffect(() => {
        let image: HTMLImageElement = null;
        if (props.siteMap) {
            const imageUrl = `${UrlUtilities.getImageBaseUrl()}${props.siteMap.MapFileImageUrl}`;

            image = new Image();
            image.onload = () => {
                const imageBounds: L.LatLngBoundsLiteral = [[-image.height / defaultZoomRatio / 2, -image.width / defaultZoomRatio / 2], [image.height / defaultZoomRatio / 2, image.width / defaultZoomRatio / 2]];
                initMapData({
                    src: imageUrl,
                    width: image.width,
                    height: image.height,
                    imageBounds: imageBounds
                });
            };

            image.onerror = () => {
                console.error('Failed to load image');
                // Handle error appropriately
            };

            image.src = imageUrl;
        }
       
        return () => {
            if (image) {
                // Clear the onload and onerror handlers to prevent state update after unmount
                image.onload = null;
                image.onerror = null;

                // Cancel the image loading
                image.src = '';
            }
        };
    }, [props.siteMap, props.views]);

    React.useEffect(() => {
        _setCenter();
    }, [props.activeView, props.overridePosition]);

    React.useEffect(() => {
        mapRef.current?.invalidateSize();
    }, [props.resizeCount]);

    const initMapData = (data: ImageData) => {
        let filteredViews: IViewMarkerData[] = [];
        if (props.siteMap && props.views) {
            const imageMidPointX = props.siteMap.ImageSize.X / 2;
            const imageMidPointY = props.siteMap.ImageSize.Y / 2;
            filteredViews = props.views.reduce<IViewMarkerData[]>((acc, view) => {
                const imagePosition = _worldToImagePosition(view.Position.X, view.Position.Z);
                const mapPosition = _imageToMapPosition(imagePosition);

                // Check if the view is within the bounds of the image
                if ((imagePosition.X + imageMidPointX) >= 0 && (imagePosition.X + imageMidPointX) <= props.siteMap.ImageSize.X &&
                    (imagePosition.Y + imageMidPointY) >= 0 && (imagePosition.Y + imageMidPointY) <= props.siteMap.ImageSize.Y) {
                    acc.push({
                        view: view,
                        imagePosition: imagePosition,
                        mapPosition: mapPosition
                    });    
                }               
                return acc;
            }, []);

        }

        let bounds: L.LatLngBoundsLiteral = data?.imageBounds ?? null;
        if (props.isMiniMap) {
            if (filteredViews.length > 0) {
                const minX = Math.min(...filteredViews.map(mv => mv.imagePosition.X));
                const maxX = Math.max(...filteredViews.map(mv => mv.imagePosition.X));
                const minY = Math.min(...filteredViews.map(mv => mv.imagePosition.Y));
                const maxY = Math.max(...filteredViews.map(mv => mv.imagePosition.Y));
                const paddingX = 20;
                const paddingY = 20;
                bounds = [[(minY - paddingY) / defaultZoomRatio, (minX - paddingX) / defaultZoomRatio], [(maxY + paddingY) / defaultZoomRatio, (maxX + paddingX) / defaultZoomRatio]];
            }
            else if (data) {
                bounds = [[-data.height / defaultZoomRatio / 2, -data.width / defaultZoomRatio / 2], [data.height / defaultZoomRatio / 2, data.width / defaultZoomRatio / 2]];
            }
        }
        setImageData(data);
        setMapViews(filteredViews);
        setMapBounds(bounds);
    }

    const _centerClick = () => {
        _setCenter();
    }

    const _setCenter = () => {
        if (imageData) {
            const center = _getImageCenter();
            setImageCenter(center);
            if (center) {
                mapRef.current?.flyTo(center);
            }
        }
    }

    // Keep a ref to the latest _setCenter so the stable map callback can call it
    // without capturing a stale closure.
    const setCenterRef = React.useRef<() => void>(null);
    setCenterRef.current = _setCenter;

    // Stable callback ref ΓÇö identity never changes across re-renders.
    // An inline lambda ref (e.g. ref={inst => fn(inst)}) gets a new identity on every
    // render; React then calls the old ref with null (cleanup) ΓåÆ _setCenter ΓåÆ
    // setImageCenter ΓåÆ re-render ΓåÆ repeat (infinite loop).
    const stableMapRef = React.useCallback((mapInstance: L.Map | null) => {
        mapRef.current = mapInstance;
        if (mapInstance) {
            setCenterRef.current?.();
        }
    }, []);

    const _worldToImagePosition = (positionX: number, positionY: number): IVector2D => {
        const imagePos = { X: 0, Y: 0 };
        const rotatedX = positionX * Math.cos(props.siteMap.Rotation) - positionY * Math.sin(props.siteMap.Rotation);
        const rotatedY = positionX * Math.sin(props.siteMap.Rotation) + positionY * Math.cos(props.siteMap.Rotation);
        if (props.siteMap.Scale) {
            imagePos.X = props.siteMap.Origin.X + (rotatedX / props.siteMap.Scale);
            imagePos.Y = props.siteMap.Origin.Y + (rotatedY / props.siteMap.Scale);
        }
        return imagePos;
    }

    const _imageToMapPosition = (imagePos: IVector2D) => {
        return new L.LatLng(imagePos.Y / defaultZoomRatio, imagePos.X / defaultZoomRatio);
    }

    const _getViewImagePositionFromVector = (position: IVector3D) => {
        // Vector3D is in Y-up coordinate system, while Leaflet uses Z-up coordinate system
        return _worldToImagePosition(position.X, position.Z);
    }

    const _getViewImageRadius = () => {
        //const radius = (view.Radius / view.ScaleFactor) / props.siteMap.Scale;
        const radius = 30 / props.siteMap.Scale / defaultZoomRatio; // 30m hardcoded (for now)

        return radius;
    }

    const _getImageCenter = () => {
        if (props.overridePosition) {
            return _imageToMapPosition(_getViewImagePositionFromVector(props.overridePosition));
        }
        else if (props.activeView) {
            return _imageToMapPosition(_getViewImagePositionFromVector(props.activeView.Position));
        }
        else {
            return null;
        }
    }

    if (imageData && mapBounds) {
        const overrideMapPosition = (props.overridePosition) ? _imageToMapPosition(_getViewImagePositionFromVector(props.overridePosition)) : null;
        const optimizeMarkerInteractions = mapViews.length >= MARKER_INTERACTION_OPTIMIZATION_THRESHOLD;
        // Leaflet fixes markerZoomAnimation when the map and its marker pane are
        // created, so remount only when the count crosses interaction modes.
        const markerInteractionMode = optimizeMarkerInteractions ? 'optimized-marker-interactions' : 'classic-marker-interactions';
        return (
            <MapContainer id="facicity-map"
                key={markerInteractionMode}
                className="map-container"
                crs={L.CRS.Simple}
                center={imageCenter}
                zoom={defaultZoom}
                bounds={mapBounds}
                style={{ flex: 1, width: '100%', height: '100%' }}
                maxZoom={maxZoom}
                minZoom={0}
                zoomSnap={0}
                zoomDelta={0.2}
                attributionControl={false}
                ref={stableMapRef}
                zoomControl={false}
                doubleClickZoom={false}
                wheelPxPerZoomLevel={80}
                markerZoomAnimation={!optimizeMarkerInteractions}
            >
                <MapMarkerInteractionController hideMarkersDuringInteraction={optimizeMarkerInteractions} />
                <CustomZoomControl isMiniMap={props.isMiniMap} setCenter={_centerClick} />

                <ImageOverlay url={imageData.src} bounds={imageData.imageBounds} />
                {mapViews.map((view, index) => {
                    const isActive = props.activeView == view.view;
                    return <View360Marker
                        isMiniMap={props.isMiniMap}
                        showElements={props.showElements}
                        key={index}
                        index={index}
                        isActive={isActive}
                        activeAsCircle={props.activeMarkerAsCircle}
                        isHighlighted={props.highlightedView == view.view}
                        name={view.view.Name}
                        position={view.mapPosition}
                        radius={_getViewImageRadius()}
                        thumbnailUrl={view.view.ThumbnailImageUrl}
                        onMarkerClicked={handleMarkerClicked}
                        onMarkerHighlighted={handleMarkerHighlighted}
                        data={view.view}
                        rotationAngle={isActive ? props.viewerDirectionDegrees : 0}
                        hasRotation={isScanPositionView(view.view)}
                    />
                })}
                {props.overridePosition && <View360Marker
                        isMiniMap={props.isMiniMap}
                        showElements={false}
                        key={-1}
                        index={-1}
                        isActive={true}
                        isHighlighted={false}
                        name={""}
                        position={overrideMapPosition}
                        rotationAngle={props.viewerDirectionDegrees}
                        hasRotation={true}
                    />}
            </MapContainer>
        );
    }
    else {
        if (props.siteMap) {
            return <Inovx.Shared.Core.ReactLoader appReady={false} />;
        }
        else {
            return <span>{InovxLoader.t('facility:message.noMapImage')}</span>;
        }
    }
}

interface CustomButtonProps {
    isMiniMap: boolean;
    setCenter: () => void;
};

const CustomZoomControl: React.FC<CustomButtonProps> = (props) => {

    const map = useMap();
    const setCenterCallbackRef = React.useRef<() => void>(null);

    const zoomIn = () => {
        map.zoomIn();
    };

    const zoomOut = () => {
        map.zoomOut();
    };
    const customButton = () => {
        setCenterCallbackRef.current?.();
    };

    React.useEffect(() => {
        setCenterCallbackRef.current = props.setCenter;
    }, [props.setCenter]);

    const createCustomControl = () => {
        const CustomControl = L.Control.extend({
            onAdd: () => {
                const container = L.DomUtil.create('div', 'leaflet-bar');

                const zoomInButton = L.DomUtil.create('button', 'leaflet-control-zoom-in leaflet-control-zoom-button', container);
                zoomInButton.innerHTML = '+';
                zoomInButton.onclick = zoomIn;

                const zoomOutButton = L.DomUtil.create('button', 'leaflet-control-zoom-out leaflet-control-zoom-button', container);
                zoomOutButton.innerHTML = '-';
                zoomOutButton.onclick = zoomOut;

                const imageUrl = `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/Recenter.png`;
                const imageTag = `<img src="${imageUrl}">`;
                if (props.isMiniMap) {
                    const customButtonElement = L.DomUtil.create('button', 'leaflet-control-zoom-in leaflet-control-zoom-button', container);
                    customButtonElement.innerHTML = imageTag;
                    customButtonElement.onclick = customButton
                }

                return container;
            },
        });
        return new CustomControl({ position: 'topleft' });
    };

    React.useEffect(() => {
        createCustomControl().addTo(map);
    }, [map]);

    return null;
}

export default FacilityMap;
